import React, { useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { Calendar, ChevronDown } from 'lucide-react';
import Button from '../Button';

const DateRangePicker = ({ startDate, endDate, onDateChange, label = "Select Date Range", allowFuture = true, align = "right", maxDays = null, hideLabel = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const today = dayjs().format('YYYY-MM-DD');
    const popoverRef = useRef(null);

    // Sync local state with parent
    const [localStart, setLocalStart] = useState(startDate);
    const [localEnd, setLocalEnd] = useState(endDate);

    useEffect(() => {
        setLocalStart(startDate);
        setLocalEnd(endDate);
    }, [startDate, endDate]);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleApply = () => {
        let finalStart = localStart;
        if (maxDays && localStart && localEnd && dayjs(localEnd).diff(dayjs(localStart), 'day') > maxDays) {
            finalStart = dayjs(localEnd).subtract(maxDays, 'day').format('YYYY-MM-DD');
            setLocalStart(finalStart);
        }
        onDateChange(finalStart, localEnd);
        setIsOpen(false);
    };

    const handlePreset = (days) => {
        const end = dayjs().format('YYYY-MM-DD');
        const start = dayjs().subtract(days, 'day').format('YYYY-MM-DD');
        setLocalStart(start);
        setLocalEnd(end);
        onDateChange(start, end);
        setIsOpen(false);
    };

    const formatDateDisplay = () => {
        if (!startDate || !endDate) return "Select date range";
        return `${dayjs(startDate).format('MMM D, YYYY')} – ${dayjs(endDate).format('MMM D, YYYY')}`;
    };

    const getMaxEnd = () => {
        let maxVal = !allowFuture ? today : undefined;
        if (maxDays && localStart) {
            const maxDaysAhead = dayjs(localStart).add(maxDays, 'day').format('YYYY-MM-DD');
            if (!maxVal || maxDaysAhead < maxVal) {
                maxVal = maxDaysAhead;
            }
        }
        return maxVal;
    };

    return (
        <div className="relative inline-block w-full sm:w-auto" ref={popoverRef}>
             {/* Trigger Button */}
            <div className="flex flex-col gap-1 w-full sm:w-auto">
                {label && !hideLabel && <label className="stride-form-label !mb-0">{label}</label>}
                <button 
                    type="button"
                    className="stride-filter-button justify-between min-w-[260px] py-2"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                >
                    <div className="flex items-center gap-2">
                        <Calendar size={15} className="text-primary shrink-0" />
                        <span className="font-semibold text-stride-heading text-xs md:text-sm">{formatDateDisplay()}</span>
                    </div>
                    <ChevronDown size={14} className={`text-stride-muted transition-transform duration-150 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Popover */}
            {isOpen && (
                <div className={`absolute z-50 mt-2 bg-white rounded-xl shadow-xl border border-border flex flex-col md:flex-row min-w-[320px] overflow-hidden ${align === 'left' ? 'left-0 origin-top-left' : 'right-0 origin-top-right'} md:w-max`}>
                    {/* Presets Sidebar */}
                    <div className="bg-surface-subtle p-3 border-r border-border flex flex-col gap-1 w-full md:w-36 shrink-0">
                        <p className="stride-eyebrow !text-[10px] px-2 mb-1">Presets</p>
                        {(!maxDays || maxDays >= 7) && (
                            <button
                                type="button"
                                onClick={() => handlePreset(7)}
                                className="text-left px-2.5 py-1.5 text-xs font-medium text-stride-body hover:bg-primary-soft hover:text-primary rounded-lg transition-colors w-full cursor-pointer border-0 bg-transparent"
                            >
                                Last 7 Days
                            </button>
                        )}
                        {(!maxDays || maxDays >= 14) && (
                            <button
                                type="button"
                                onClick={() => handlePreset(14)}
                                className="text-left px-2.5 py-1.5 text-xs font-medium text-stride-body hover:bg-primary-soft hover:text-primary rounded-lg transition-colors w-full cursor-pointer border-0 bg-transparent"
                            >
                                Last 14 Days
                            </button>
                        )}
                        {(!maxDays || maxDays >= 30) && (
                            <button
                                type="button"
                                onClick={() => handlePreset(30)}
                                className="text-left px-2.5 py-1.5 text-xs font-medium text-stride-body hover:bg-primary-soft hover:text-primary rounded-lg transition-colors w-full cursor-pointer border-0 bg-transparent"
                            >
                                Last 30 Days
                            </button>
                        )}
                        {(!maxDays || maxDays >= 90) && (
                            <button
                                type="button"
                                onClick={() => handlePreset(90)}
                                className="text-left px-2.5 py-1.5 text-xs font-medium text-stride-body hover:bg-primary-soft hover:text-primary rounded-lg transition-colors w-full cursor-pointer border-0 bg-transparent"
                            >
                                Last 90 Days
                            </button>
                        )}
                    </div>

                    {/* Custom Range Configuration */}
                    <div className="p-5 flex flex-col gap-4 bg-white flex-1 min-w-[280px]">
                        <p className="stride-eyebrow !text-[10px] mb-0">Custom Range</p>
                        <div className="flex flex-col sm:flex-row gap-3 items-center">
                            <div className="flex flex-col gap-1 w-full">
                                <label className="text-xs font-medium text-stride-muted">Start</label>
                                <input
                                    type="date"
                                    value={localStart}
                                    max={localEnd || (!allowFuture ? today : undefined)}
                                    onChange={(e) => setLocalStart(e.target.value)}
                                    className="stride-input-field text-xs py-1.5"
                                />
                            </div>
                            <span className="text-stride-muted-light hidden sm:block mt-5">–</span>
                            <div className="flex flex-col gap-1 w-full">
                                <label className="text-xs font-medium text-stride-muted">End</label>
                                <input
                                    type="date"
                                    value={localEnd}
                                    min={localStart}
                                    max={getMaxEnd()}
                                    onChange={(e) => setLocalEnd(e.target.value)}
                                    className="stride-input-field text-xs py-1.5"
                                />
                            </div>
                        </div>

                        {maxDays && (
                            <p className="text-[11px] text-stride-muted m-0">
                                Maximum range: {maxDays} days
                            </p>
                        )}

                        <div className="flex justify-end items-center mt-2 pt-3 border-t border-border-subtle gap-2">
                            <Button 
                                variant="ghost"
                                size="xs"
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="primary"
                                size="xs"
                                onClick={handleApply}
                                disabled={!localStart || !localEnd}
                            >
                                Apply Range
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
